import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertBox,
  BasicModal,
  SelectInput,
  TextInput,
} from "@life-line-lab/components-react";
import RoleModalContent from "../../role-modal-content/role-modal-content";
import { Col, Row } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { UserCreationInput, UserRoleToAssign } from "Types/user.types";
import { Option } from "Types/common.types";
import useCorporateType from "Hooks/corporate-type.hook";

interface EmploymentDetailsProps {
  input: UserCreationInput;
  error: any;
  isOrgCustomer?: boolean;
  locationOptions?: Option[];
  onTextInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectInputChange?: (value: string | string[], roles: string) => void;
}

const EmploymentDetails: React.FC<EmploymentDetailsProps> = ({
  input,
  error,
  isOrgCustomer = false,
  locationOptions,
  onTextInputChange,
  onSelectInputChange,
}) => {
  const { t } = useTranslation();
  const { isCustomer } = useCorporateType();

  const userRoles = useMemo(() => {
    return isCustomer
      ? [
          {
            label: t("label.admin"),
            value: UserRoleToAssign.ADMIN,
          },
          {
            label: t("label.operationStaff"),
            value: UserRoleToAssign.OPERATIONAL_STAFF,
          },
        ]
      : [
          {
            label: t("label.admin"),
            value: UserRoleToAssign.ADMIN,
          },
          {
            label: t("label.operationSupervisor"),
            value: UserRoleToAssign.OPERATION_SUPERVISOR,
          },
          {
            label: t("label.operationStaff"),
            value: UserRoleToAssign.OPERATIONAL_STAFF,
          },
          {
            label: t("label.publicTransportOperator"),
            value: UserRoleToAssign.PUBLIC_TRANSPORT_OPERATIONAL_USER,
          },
        ];
  }, []);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  return (
    <>
      {input.id ? null : (
        <div className="center header-title">
          {t("title.employmentDetails").toUpperCase()}
        </div>
      )}
      <Row gutter={[15, 15]} className="mt-50">
        {isOrgCustomer && (
          <Col xs={24} sm={24} md={12}>
            <TextInput
              isReadonly
              isRequired={true}
              label={t("label.company").toUpperCase()}
              value={input.company}
              name="company"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onTextInputChange && onTextInputChange(event);
              }}
            />
            <AlertBox text={error.company} />
          </Col>
        )}

        {isOrgCustomer && (
          <Col xs={24} sm={24} md={12}>
            <SelectInput
              showSearch
              options={locationOptions}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >=
                0
              }
              value={input.location}
              name="location"
              label={t("label.location")}
              placeholder={t("option.location")}
              isRequired={true}
              onChange={(value: string | string[]) => {
                onSelectInputChange && onSelectInputChange(value, "location");
              }}
            />
            <AlertBox text={error.location} />
          </Col>
        )}

        <Col xs={24} sm={24} md={12}>
          <TextInput
            isReadonly={
              input.subType?.replace(" ", "_") === UserRoleToAssign.SUPER_ADMIN
            }
            label={t("label.department")}
            value={input.department}
            name="department"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onTextInputChange && onTextInputChange(event);
            }}
          />
          <AlertBox text={error.department} />
        </Col>

        <Col xs={24} sm={24} md={12}>
          <SelectInput
            showSearch
            options={userRoles}
            filterOption={(inputValue, option) =>
              option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
            }
            isReadonly={
              input.subType?.replace(" ", "_") === UserRoleToAssign.SUPER_ADMIN
            }
            value={input.subType}
            name="roles"
            label={t("label.subType")}
            suffixIcon={
              <InfoCircleOutlined
                onClick={() => {
                  setIsModalOpen(true);
                }}
              />
            }
            placeholder={t("placeholder.subType")}
            isRequired={true}
            onChange={(value: string | string[]) => {
              onSelectInputChange && onSelectInputChange(value, "subType");
            }}
          />
          <AlertBox text={error.subType} />
        </Col>
      </Row>

      <BasicModal
        isOpen={isModalOpen}
        isClosable={true}
        isMaskClosable={true}
        width={850}
        onClose={() => {
          setIsModalOpen(false);
        }}
      >
        <RoleModalContent />
      </BasicModal>
    </>
  );
};

export default EmploymentDetails;
